import { getApiBaseUrl } from "./baseUrl";
const API_BASE = getApiBaseUrl();

export const voicePlayAudioStreamAuto = async (
  token: string,
  comment_type: string,
  ai_comments_id: number,
  partner_voice_name: string,
  audio: HTMLAudioElement
) => {
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const mime = isSafari ? 'audio/mp4; codecs="mp4a.40.2"' : 'audio/webm; codecs="opus"';
  const format = isSafari ? "mp4" : "webm";

  if (!MediaSource.isTypeSupported(mime)) {
    throw new Error(`このブラウザは ${mime} をサポートしていません`);
  }

  const res = await fetch(`${API_BASE}/api/management/voice?format=${format}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ comment_type, ai_comments_id, partner_voice_name }),
  });

  if (!res.body) throw new Error("音声ストリームが受信できません");

  const mediaSource = new MediaSource();
  audio.pause();
  audio.src = URL.createObjectURL(mediaSource);
  audio.load();

  mediaSource.addEventListener("sourceopen", async () => {
    const sourceBuffer = mediaSource.addSourceBuffer(mime);
    const reader = res.body!.getReader();
    const queue: Uint8Array[] = [];
    let totalBytes = 0;

    const waitForUpdateEnd = () =>
      new Promise<void>((resolve) =>
        sourceBuffer.addEventListener("updateend", () => resolve(), { once: true })
      );

    const flushQueue = async () => {
      while (queue.length > 0) {
        if (!sourceBuffer.updating) {
          const chunk = queue.shift()!;
          sourceBuffer.appendBuffer(chunk as any);
          await waitForUpdateEnd();
        } else {
          await waitForUpdateEnd();
        }
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        console.log("✅ 全チャンクの読み取りが完了しました");
        break;
      }
      if (value) {
        queue.push(value);
        totalBytes += value.length;
      }

      if (!sourceBuffer.updating && queue.length > 0) {
        const chunk = queue.shift()!;
        sourceBuffer.appendBuffer(chunk as any);
        await waitForUpdateEnd();
      }
    }

    await flushQueue();

    const endStream = () => {
      if (!sourceBuffer.updating && mediaSource.readyState === "open") {
        mediaSource.endOfStream();
        console.log("📦 MediaSource.endOfStream() 呼び出し");
      } else {
        sourceBuffer.addEventListener("updateend", endStream, { once: true });
      }
    };
    endStream();

    const waitUntilReadyToPlay = () =>
      new Promise<void>((resolve) => {
        const check = () => {
          if (mediaSource.readyState === "ended") {
            console.log("🔵 MediaSource.readyState = 'ended'");
            resolve();
          } else {
            setTimeout(check, 100);
          }
        };
        check();
      });

    console.log(`📦 全体で受信した音声データサイズ: ${totalBytes} bytes`);

    audio.addEventListener("pause", () => {
      if (audio.ended) {
        console.log("✅ audio.pause() は再生完了に伴うものです");
      } else {
        console.warn("⏸ audio.pause() されました（途中停止検知）");
      }
    });
    audio.addEventListener("waiting", () => {
      console.warn("🟡 バッファ不足 waiting イベント発生");
    });
    audio.addEventListener("stalled", () => {
      console.warn("🔴 stalled（再生停止）イベント発生");
    });
    audio.addEventListener("suspend", () => {
      console.warn("suspend（再生停止）イベント発生");
    });
    audio.addEventListener("ended", () => {
      console.log("✅ 再生完了 ended イベント");
    });

    await waitUntilReadyToPlay();

    try {
      await audio.play();
      console.log("▶️ audio.play() 開始");
    } catch (err) {
      console.error("❌ audio.play() エラー", err);
    }
  });
};

/**
 * Web環境ではExpo Audio用のBase64 URIは使用しないため、
 * エラーを投げるか、必要に応じてWeb用の実装を行います。
 */
export const fetchVoiceBase64Uri = async (
  token: string,
  comment_type: string,
  ai_comments_id: number,
  partner_voice_name: string
): Promise<string> => {
  throw new Error("fetchVoiceBase64Uri is not supported on web environments.");
};